import { combineReducers } from 'redux';
import workspaceParser from '../../services/workspaceParser';
import { ActionWithOptionalPayload } from '../typings';
import { insertItemIntoArray, removeItemFromArray } from '../../util/array/arrayUtil';
import {
    SplitPreviewItem,
    WorkspaceOverlayConfiguration, WorkspacePreset, WorkspaceSchemeModel, WorkspaceTypeKeys,
} from './types';
import { CommonContextActionTypes } from '../context/actionTypes';

export type ContextMenuConfig = {
    showHeader: boolean;
    isExpanded: boolean;
    visible: boolean;
};

export type GenericFeatureState = {
    readonly default: ContextMenuConfig;
    [key: string]: ContextMenuConfig;
};

export type FeatureStatusMap = {
    [key: string]: string;
};

export type WorkspacesState = {
    readonly currentWorkspace: WorkspacePreset;
    readonly workspaceScheme: WorkspaceSchemeModel;
    readonly genericFeatureState: GenericFeatureState;
    readonly featuresLoading: string[];
    readonly activeOverlay: string;
    readonly focusMode: boolean;
    readonly printMode: boolean;
    readonly fullScreenMode: boolean;
    readonly featureStatus: FeatureStatusMap;
    readonly featuresCount: number;
    readonly workspaceOverlay?: WorkspaceOverlayState;
    readonly currentRequiredContextProperties?: string[];
    readonly sideBarExpanded: boolean;
    readonly structureEditorExpanded: boolean;
    readonly splitPreviewItem?: SplitPreviewItem;
};

export type WorkspaceOverlayState = {
    readonly workspaceOverlayName: string;
    readonly targetNodeId: string;
    readonly workspaceOverlayConfiguration: WorkspaceOverlayConfiguration;
};

export const initialState: WorkspacesState = {
    currentWorkspace: null,
    workspaceScheme: null,
    activeOverlay: null,
    genericFeatureState: {
        default: {
            showHeader: true,
            isExpanded: false,
            visible: true,
        },
    },
    featuresLoading: [],
    currentRequiredContextProperties: [],
    focusMode: false,
    printMode: false,
    fullScreenMode: false,
    featureStatus: {},
    featuresCount: 0,
    workspaceOverlay: null,
    sideBarExpanded: true,
    splitPreviewItem: null,
    structureEditorExpanded: true,
};

export const workspacesReducer = combineReducers<WorkspacesState, ActionWithOptionalPayload>({
    currentRequiredContextProperties: (state = initialState.currentRequiredContextProperties, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.RESET_CONTEXT_REQUIRED_PROPERTIES:
            return [];
        case WorkspaceTypeKeys.SET_CONTEXT_REQUIRED_PROPERTIES:
            if (action.payload && action.payload.length > 0) {
                return [...state, ...action.payload.filter((prop: string): boolean => !state.includes(prop))];
            }
            return state;
        default:
            return state;
        }
    },
    activeOverlay: (state = initialState.activeOverlay, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_ACTIVE_OVERLAY:
            return action.payload;
        case WorkspaceTypeKeys.RESET_ACTIVE_OVERLAY:
            return null;
        default:
            return state;
        }
    },
    fullScreenMode: (state = initialState.fullScreenMode, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.TOGGLE_FULL_SCREEN:
            return !state;
        default:
            return state;
        }
    },
    featuresCount: (state = initialState.featuresCount, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.WORKSPACE_PRESET_LOADED:
            return action.payload
                ? workspaceParser.getFeaturesCount(action.payload) : 0;
        case WorkspaceTypeKeys.FEATURE_LOADED:
            return state - 1 < 0 ? 0 : state - 1;
        default:
            return state;
        }
    },
    featuresLoading: (state = initialState.featuresLoading, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_FEATURE_LOADING:
            return insertItemIntoArray(state, action.payload);
        case WorkspaceTypeKeys.STOP_FEATURE_LOADING:
            return removeItemFromArray(state, action.payload);
        case CommonContextActionTypes.WORKSPACE_ID_UPDATED:
            return initialState.featuresLoading;
        default:
            return state;
        }
    },
    featureStatus: (state = initialState.featureStatus, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_FEATURE_STATUS_INDICATOR:
            return {
                ...state,
                [action.payload.featureId]: action.payload.status,
            };
        default:
            return state;
        }
    },
    printMode: (state = initialState.printMode, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_PRINT_MODE:
            return action.payload;
        default:
            return state;
        }
    },
    focusMode: (state = initialState.focusMode, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.TOGGLE_FOCUS_MODE:
            return !state;
        default:
            return state;
        }
    },
    genericFeatureState: (state = initialState.genericFeatureState, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_CONTEXT_MENU_VISIBILITY:
            return {
                ...state,
                [action.payload]: {
                    ...state[action.payload],
                    visible: !state[action.payload].visible,
                },
            };
        case WorkspaceTypeKeys.SETUP_GENERIC_FEATURES_STATE:
            return {
                ...state,
                [action.payload]: {
                    ...state.default,
                },
            };
        case WorkspaceTypeKeys.TOGGLE_HEADER:
            return {
                ...state,
                [action.payload]: {
                    ...state[action.payload],
                    showHeader: !state[action.payload].showHeader,
                },
            };
        case WorkspaceTypeKeys.TOGGLE_EXPAND:
            const featureId = typeof action.payload === 'string' ? action.payload : action.payload?.featureId;
            const newValue = typeof action.payload === 'object' ? action.payload?.newValue : !state[featureId].isExpanded;
            return {
                ...state,
                [featureId]: {
                    ...state[featureId],
                    isExpanded: newValue,
                },
            };
        default:
            return state;
        }
    },

    workspaceScheme: (state = initialState.workspaceScheme, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.WORKSPACE_SCHEME_LOADED:
            return action.payload;
        default:
            return state;
        }
    },

    currentWorkspace: (state = initialState.currentWorkspace, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.WORKSPACE_PRESET_LOADED:
            return action.payload;
        default:
            return state;
        }
    },

    workspaceOverlay: (state = initialState.workspaceOverlay, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.SET_WORKSPACE_OVERLAY:
            return {
                ...state,
                workspaceOverlayName: action.payload.workspaceOverlayName,
                workspaceOverlayConfiguration: action.payload.workspaceOverlayConfiguration,
                targetNodeId: action.payload.targetNodeId,
            };
        case WorkspaceTypeKeys.CLOSE_WORKSPACE_OVERLAY:
            return initialState.workspaceOverlay;

        case WorkspaceTypeKeys.IS_CLOSED_CLICKED:
            return {
                ...state,
                workspaceOverlayConfiguration: {
                    ...state.workspaceOverlayConfiguration,
                    options: {
                        ...state.workspaceOverlayConfiguration.options,
                        isClosedClicked: action.payload,
                    },
                },
            };
        case WorkspaceTypeKeys.HAS_BEEN_EDITED:
            return {
                ...state,
                workspaceOverlayConfiguration: {
                    ...state.workspaceOverlayConfiguration,
                    options: {
                        ...state.workspaceOverlayConfiguration.options,
                        hasBeenEdited: action.payload,
                    },
                },
            };
        default:
            return state;
        }
    },
    sideBarExpanded: (state = initialState.sideBarExpanded, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.TOGGLE_SIDE_BAR:
            return action.payload;
        default:
            return state;
        }
    },
    splitPreviewItem: (state = initialState.splitPreviewItem, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.OPEN_ITEM_IN_SPLIT_VIEW:
            return action.payload;
        default:
            return state;
        }
    },
    structureEditorExpanded: (state = initialState.structureEditorExpanded, action) => {
        switch (action.type) {
        case WorkspaceTypeKeys.TOGGLE_STRUCTURE_EDITOR:
            return action.payload;
        default:
            return state;
        }
    },
});
